/**
 * @file AppShell — wraps the app in the design's `cd-root` / `cd-app` grid.
 *
 * Layout:
 *   .cd-root
 *     .cd-app[data-side="auto|full|rail|hidden"]
 *       .cd-sidebar (desktop only via design's CSS media queries)
 *       .cd-main (children)
 *     .cd-mobile-overlay (when open)
 *
 * Auth pages, public pages, onboarding etc. render bare (no shell).
 * Patient and Admin shells are kept (PatientLayout / AdminLayout).
 */
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import AppSidebar from "./AppSidebar";
import AppTopbar from "./AppTopbar";
import { usePendingCount } from "./NotificationsMenu";

const NO_SHELL_PATHS = [
  "/login",
  "/login/profesional",
  "/login/paciente",
  "/patient/login",
  "/register/professional",
  "/register/patient",
  "/verify-code",
  "/verify-email",
  "/forgot-password",
  "/reset-password",
  "/aceptar-terminos",
  "/onboarding",
];

const TopbarIcon = {
  Menu: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M3 6h18M3 12h18M3 18h18" />
    </svg>
  ),
  X: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  ),
  Bell: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10 21a2 2 0 0 0 4 0" />
    </svg>
  ),
  Calendar: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 9h18M8 3v4M16 3v4" />
    </svg>
  ),
};

function shouldHideShell(path) {
  if (NO_SHELL_PATHS.some((p) => path === p || path.startsWith(`${p}/`))) return true;
  if (path.startsWith("/c/")) return true;
  if (path.startsWith("/cita-detalle/")) return true;
  if (path.startsWith("/cita/confirmar/")) return true;
  if (path.startsWith("/cita/gestionar/")) return true;
  if (path.startsWith("/verificar-email")) return true;
  return false;
}

function MobileBar({ onOpen }) {
  const navigate = useNavigate();
  const pendingCount = usePendingCount();
  return (
    <div className="cd-mobile-bar">
      <button type="button" className="cd-hamburger" aria-label="Abrir menú" onClick={onOpen}>
        <TopbarIcon.Menu />
      </button>
      <Link to="/" className="cd-mobile-brand" aria-label="Ir a inicio">
        <div className="cd-brand-mark" style={{ width: 26, height: 26, borderRadius: 7 }}>
          <span />
        </div>
        <div className="cd-brand-name" style={{ fontSize: 16 }}>Consultto</div>
      </Link>
      <div className="cd-mobile-actions">
        <button
          type="button"
          className="cd-icon-btn"
          aria-label="Notificaciones"
          onClick={() => navigate("/citas")}
        >
          <TopbarIcon.Bell />
          {pendingCount > 0 && <span className="cd-dot" />}
        </button>
        <button
          type="button"
          className="cd-icon-btn"
          aria-label="Calendario"
          onClick={() => navigate("/calendario")}
        >
          <TopbarIcon.Calendar />
        </button>
      </div>
    </div>
  );
}

function MobileOverlay({ open, onClose, user, doctor, pendingCount, onLogout }) {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="cd-mobile-overlay" data-open="true" onClick={onClose}>
      <div className="cd-mobile-panel" onClick={(e) => e.stopPropagation()}>
        <div className="cd-mobile-panel-head">
          <div className="cd-brand">
            <div className="cd-brand-mark"><span /></div>
            <div className="cd-brand-name">Consultto</div>
          </div>
          <button type="button" className="cd-mobile-panel-close" aria-label="Cerrar" onClick={onClose}>
            <TopbarIcon.X />
          </button>
        </div>
        <AppSidebar
          user={user}
          doctor={doctor}
          pendingCount={pendingCount}
          onItemClick={onClose}
          onLogout={onLogout}
        />
      </div>
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    setMobileOpen(false);
    await logout();
    navigate("/login");
  };

  if (!isAuthenticated || shouldHideShell(location.pathname)) {
    return <div className="cd-bare">{children}</div>;
  }

  if (user?.role !== "PROFESIONAL") {
    return <div className="cd-bare">{children}</div>;
  }

  const isInicio = location.pathname === "/" || location.pathname === "/home";

  const Footer = () => (
    <>
      <div style={{ display: "flex", gap: 18 }}>
        <a>Aviso de privacidad</a>
        <a>Términos y condiciones</a>
        <a>Soporte</a>
      </div>
      <div>© 2026 Consultto</div>
    </>
  );

  return (
    <div className="cd-root">
      <div className="cd-app">
        <AppSidebar user={user} onLogout={handleLogout} />
        <main className="cd-main">
          <MobileBar onOpen={() => setMobileOpen(true)} />
          {isInicio ? (
            <>
              <AppTopbar />
              {children}
            </>
          ) : (
            <div className="pg-main">
              {children}
              <footer className="pg-foot"><Footer /></footer>
            </div>
          )}
          {isInicio && <footer className="cd-foot"><Footer /></footer>}
        </main>
      </div>
      <MobileOverlay
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        user={user}
        onLogout={handleLogout}
      />
    </div>
  );
}
